import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import APIS from "@service/api";
import { getUser } from "@utils";

export const chatManagementApi = createApi({
	reducerPath: "chatManagementApi",
	refetchOnFocus: false,
	refetchOnReconnect: true,
	refetchOnMountOrArgChange: false,
	baseQuery: fetchBaseQuery({
		baseUrl: APIS["chat-api"],
		prepareHeaders: (headers) => {
			const user = getUser();

			if (user?.token) {
				headers.set("Authorization", `Bearer=${user.token}`);
			}

			return headers;
		},
	}),
	endpoints: (builder) => ({
		getChatEnabled: builder.query({
			query: () => ({
				url: "chat-enabled",
				method: "GET",
			}),
		}),
		getChatEligibility: builder.query({
			query: () => ({
				url: "eligibility",
				method: "GET",
			}),
		}),
		getChatChannels: builder.query({
			query: () => ({
				url: "channels",
				method: "GET",
			}),
		}),
		getChatMessages: builder.infiniteQuery({
			query: ({ pageParam, queryArg }) => ({
				url: `channels/${encodeURIComponent(queryArg.channelId)}/messages`,
				method: "GET",
				params: {
					limit: queryArg.limit ?? 50,
					...(pageParam ? { lastKey: pageParam } : {}),
				},
			}),
			infiniteQueryOptions: {
				getNextPageParam: (lastPage) => lastPage?.lastKey ?? undefined,
			},
		}),
		sendChatMessage: builder.mutation({
			query: ({ channelId, text, mentions, replyToMessageId }) => ({
				url: `channels/${encodeURIComponent(channelId)}/messages`,
				method: "POST",
				body: {
					text,
					...(mentions?.length ? { mentions } : {}),
					...(replyToMessageId ? { replyToMessageId } : {}),
				},
			}),
		}),
		sendChatMedia: builder.mutation({
			query: ({ channelId, mediaUrl, contentType, text, mentions, replyToMessageId }) => ({
				url: `channels/${encodeURIComponent(channelId)}/messages/media`,
				method: "POST",
				body: {
					mediaUrl,
					contentType,
					...(text ? { text } : {}),
					...(mentions?.length ? { mentions } : {}),
					...(replyToMessageId ? { replyToMessageId } : {}),
				},
			}),
		}),
		shareChatMessage: builder.mutation({
			query: ({ channelId, transactionId, shareType }) => ({
				url: `channels/${encodeURIComponent(channelId)}/messages/share`,
				method: "POST",
				body: { transactionId, shareType },
			}),
		}),
		shareLatestCasinoWin: builder.mutation({
			query: ({ channelId }) => ({
				url: `channels/${encodeURIComponent(channelId)}/messages/share/latest-casino-win`,
				method: "POST",
			}),
		}),
		deleteChatMessage: builder.mutation({
			query: ({ channelId, messageId }) => ({
				url: `channels/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(
					messageId
				)}`,
				method: "DELETE",
			}),
		}),
		getChatMediaUploadUrl: builder.mutation({
			query: (contentType) => ({
				url: "media/upload-url",
				method: "GET",
				params: { contentType },
			}),
		}),
	}),
});

export const {
	useGetChatEnabledQuery,
	useGetChatEligibilityQuery,
	useGetChatChannelsQuery,
	useGetChatMessagesInfiniteQuery,
	useSendChatMessageMutation,
	useSendChatMediaMutation,
	useShareChatMessageMutation,
	useShareLatestCasinoWinMutation,
	useDeleteChatMessageMutation,
	useGetChatMediaUploadUrlMutation,
} = chatManagementApi;

/**
 * Upload file to S3 presigned URL
 * @param {string} uploadUrl
 * @param {File} file
 */
export const uploadChatMediaToS3 = async (uploadUrl, file) => {
	const response = await fetch(uploadUrl, {
		method: "PUT",
		body: file,
		headers: {
			"Content-Type": file.type,
		},
	});

	if (!response.ok) {
		throw new Error("Failed to upload media");
	}
};
